import { createI18n } from "vue-i18n";
import { I18n } from "aws-amplify/utils";
import { translations } from "@aws-amplify/ui-vue";

const userLocale =
    navigator.languages && navigator.languages.length
        ? navigator.languages[0]
        : navigator.language || "en-GB";

const enTranslations = {
    alerts: {
        notificationSubscriptions: {
            successfullyUpdated:
                "Successfully updated your notification subscriptions.",
        },
        unexpectedErrorWithRefreshAction:
            "We have encountered an unexpected issue. Please refresh the page to continue.",
        unexpectedErrorWithRetryAction:
            "We have encountered an unexpected issue. Please try again.",
        validationError:
            "Please make sure that all the required fields are filled out correctly before continuing.",
        sessionTimeout: "Your session has timed out.",
        successfulDownload: "The file has been exported successfully.",
        csvExportLimitExceeded:
            "CSV exports are limited to {limit} payments. Please narrow the filters and try again.",
        bankAccountValidation: "Please add at least one bank account.",
        bankAccountLimit:
            "You've hit the limit on the number of bank accounts you can add.",
        existentStakeholder:
            "A stakeholder with the same name already exists. Please modify the fields to continue.",
        removedStakeholder: "The stakeholder has been removed.",
        removedConsent: "The consent for {bank} has been revoked.",
        selectPayment: "Please select a payment to continue.",
        selectMandate: "Please select a mandate to continue.",
        selectAccount: "Please select an account to continue.",
        selectProfile: "Please select a profile to continue.",
        selectFile: "No file was selected. Please select a file to continue.",
        selectDate: "Please select a date to continue.",
        mandatesUpdated: "The mandates have been updated.",
        paymentsUpdated: "Your payments have been updated.",
        instructionsUpdated: "The instructions have been updated.",
        removedInvalidReferences: "The invalid references have been removed.",
        updatedCollectionDates: "The dates have been updated.",
        uploadedFile:
            "The file has been uploaded. It might take a few minutes for the file contents to be processed.",
        uploadedLogo: "The logo has been uploaded.",
        invalidFiles: "Please ensure that the files are valid.",
        addedPaymentLink: "A new payment request link was added.",
        linkCopied: "Link copied.",
        keyCopied: "Key copied.",
        copyError: "Could not copy. Try again.",
        invalidEmail: "Please enter a valid email address to continue.",
        updatedEmail: "The email address has been updated.",
        successfulRefund: "Your refund request was successful.",
        updatedPostingPreferences: "The posting preferences have been updated.",
        generatedApiKeys: "Successfully generated the API keys.",
        cannotModifyMandate:
            "{failedCount} mandate{plural} cannot be modified. Please deselect them and try again.",
        cannotApproveMandateWithNotification:
            "{failedCount} mandate{plural} cannot be approved with notification. Either the email address is missing for this record, or the collection date is too soon.",
        cannotModifyPayment:
            "{failedCount} payment{plural} cannot be modified. Please deselect them and try again.",
        cannotApprovePaymentWithNotification:
            "{failedCount} payment{plural} cannot be approved with notification. Either the email address is missing for this record, or the collection date is too soon.",
        authenticationRequired: "Permissions Updated: Action Required",
        accessUpdated:
            "Your account permissions have been updated. To ensure these changes take effect, please log out and then log back in. This will refresh your access and apply the latest permissions.",
        selectCheckbox: "Please select the checkbox to confirm.",
        overdueInstruction:
            "{count} {instruction} {verb} overdue. Please update the {date} to continue.",
        batchUpdated: "The batch has been updated.",
        batchDiscarded: "The batch has been discarded.",
        batchApproved: "The batch has been approved.",
        invalidBatchName: "Please enter a valid name for your batch.",
        approvalsNeeded:
            "You cannot proceed until all approvers have approved the batch.",
        actionInstruction:
            "To proceed to the next step, all instructions marked as 'Action Required' must be either included in or excluded from the batch.",
        includedInstructions:
            "To proceed to the next step, please add at least one instruction. If you don’t want to send this batch, you can discard it.",
        organisationCreated: "The organisation has been created successfully.",
        organisationSwitched: "Switched to {organisation}.",
        cardLimitSet: "The card limit has been updated to {currency}{amount}.",
        cardLimitRemoved: "The card limit has been removed.",
        expiredSession:
            "Your session has expired. Please sign in again to continue.",
        reportStatusUpdated: "The report status has been updated",
        multipleApprovalsAreRequired:
            "You have already approved this batch. However, it still needs {noNotProvidedApprovals} more unique approval(s) out of {noRequiredApprovals} to proceed.",
        sendDiscardedBatch:
            "This batch has already been discarded and cannot be sent to the bureau.",
        uploadDocument: "The document has been uploaded.",
        submitDocsForPerson:
            "The documents for this individual have been submitted for verification.",
        detailsUpdated: "Details updated successfully",
        userCreatedSuccessfully: "The user has been successfully created.",
        userMfaResetSuccessfully:
            "The user's MFA method has been successfully reset.",
        userDeletedSuccessfully: "The user has been successfully deleted.",
        permissionsSaveFailed: "Failed to save permissions. Please try again.",
        paymentLinksUpdated: "Payment links updated successfully",
        feedbackSentSuccessfully:
            "Your feedback has been sent successfully. Thank you for your input!",
        interestRegistered:
            "We've recorded your interest in this feature. Our team will contact you shortly to follow up.",
        printFailed: "Unable to print.",
        removedBankAccount: "Unsaved bank account removed from the list.",
        recordsPostedToInvoice:
            "{processedCount} of {totalRecords} records were successfully posted to invoice. {failedCount} failed.",
        recordsPostedToBilling:
            "{processedCount} of {totalRecords} records were successfully posted to billing contact. {failedCount} failed.",
        recordsIgnored:
            "{processedCount} of {totalRecords} records were successfully ignored. {failedCount} failed.",
        sessionExpiring: "Session ending soon",
        sessionInactiveWarning:
            "We’ve detected that you’ve been inactive for a while. To protect sensitive data, you’ll be signed out in {seconds} seconds unless you choose to stay signed in.",
        voidCompletedSuccessfully:
            "The void request has been initiated. The transaction will appear in the table once processing is complete.",
        refundCompletedSuccessfully:
            "Refund request submitted. It can take a few minutes to process. Refresh the table to see it, and please don't submit the refund again.",
        bacsProfileCreated: "The BACS profile has been created successfully.",
        bacsProfileUpdated: "The BACS profile has been updated successfully.",
        bacsOrganisationAccountsRefreshed: "Your accounts are now up to date.",
        bacsAccountUpdated: "The BACS account has been updated successfully.",
        idpRemoved: "The identity provider has been removed successfully.",
        discardNotAllowed:
            "This batch cannot be discarded because it has the status {status}. It may have already been updated by another user.",
        sendToBureauNotAllowed:
            "This batch cannot be sent to the bureau because it has the status {status}. It may have already been updated by another user.",
        contactDetailsUpdated:
            "The contact details have been updated successfully.",
        apiErrors: {
            CANNOT_UPDATE_PAYMENT_ACCOUNT:
                "Updating the payment account is not possible. To choose a new account, please discard this batch and create a new one.",
            BAD_REQUEST:
                "Invalid request: Please check your input and try again. Contact support if the issue persists.",
            UNAUTHORIZED:
                "Access to this resource is denied. Please log in again.",
            FORBIDDEN:
                "You do not have authorisation to view these resources. If you believe this is an error, please log out and log back in. For further assistance, please contact support.",
            NOT_FOUND: "The resource you were looking for could not be found.",
            METHOD_NOT_ALLOWED:
                "Action not allowed: This request method is not supported. Please contact support if you need assistance.",
            INTERNAL_SERVER_ERROR_REFRESH:
                "We have encountered an unexpected issue. Please refresh the page to continue.",
            INTERNAL_SERVER_ERROR_RETRY:
                "We have encountered an unexpected issue. Please try again.",
            UNKNOWN_ERROR_REFRESH:
                "We have encountered an unexpected issue. Please refresh the page to continue.",
            UNKNOWN_ERROR_RETRY:
                "We have encountered an unexpected issue. Please try again.",
            TOO_MANY_REQUESTS: "Too many requests, please try again later.",
            NETWORK_ERROR:
                "No internet connection. Please check your network connection.",
            FILE_TOO_LARGE:
                "File too large. Please upload a smaller one or contact support for assistance.",
            SUCCESSFUL_DOWNLOAD: "The file has been exported successfully.",
            DUPLICATE_PAYMENT_REFERENCE: "The payment reference is not unique.",
            DUPLICATE_EMAIL_ADDRESS:
                "This email address is already registered with another user account.",
            TRANSACTIONS_DAILY_LIMIT_EXCEEDED:
                "The transaction total exceeds the daily limit.",
            DUPLICATE_ORGANISATION:
                "An organisation with this name already exists. Please choose a different name or reach out to our support team for assistance.",
            ORGANISATION_NOT_FOUND:
                "The organisation could not be found. Please contact support if you need assistance.",
            INVALID_APPROVER:
                "You have already approved this instruction and cannot perform the second approval.",
            EMAIL_REPORT:
                "The report is too large to download immediately. You will receive an email with a download link in a few minutes once it's ready.",
            EMAIL_DOMAIN_DOES_NOT_MATCH:
                "The email address you entered does not match the allowed domain. Please use a valid company email address.",
            USER_ALREADY_EXISTS:
                "A user with this email already exists. Please use a different email address or update the existing user.",
            INVALID_PERMISSIONS_CONFIGURATION:
                "The permissions configuration is invalid. Please review and adjust the selected permissions.",
            CANNOT_EDIT_OWN_USER:
                "You cannot edit your own user account. Please ask another administrator to make this change.",
            WCBS_UNAVAILABLE:
                "We cannot connect to your PASS instance. Check your API settings inside of Settings > Finance systems.",
            TRANSACTION_NOT_ELIGIBLE_FOR_REFUND:
                "This transaction is not eligible for a refund, and we are unable to issue a refund at this time.",
            IDENTITY_PROVIDER_ALREADY_EXISTS_FOR_ORGANISATION:
                "This identity provider already exists for this organisation. Please choose a different provider or contact support for assistance.",
            INVALID_URL:
                "The URL provided is invalid. Please enter a valid URL.",
            PORTAL_USER_NOT_FOUND:
                "Your account does not have access to this organisation. Please contact your organisation administrator to request access.",
            EMAIL_NOT_VERIFIED_FOR_PORTAL_USER:
                "Your email address has not been verified. Please verify your email address before signing in.",
            OIDC_WELL_KNOWN_ENDPOINT_UNREACHABLE:
                "We are unable to reach the OIDC well-known endpoint. Check the URL and try again, or see the setup support guide for help.",
            CANNOT_DISCARD_BATCH:
                "Unable to discard batch due to its current status. Refresh the page to view the latest batch status.",
            CANNOT_APPROVE_BATCH:
                "Unable to approve batch due to its current status. Refresh the page to view the latest batch status.",
            CANNOT_UNAPPROVE_BATCH:
                "Unable to unapprove batch due to its current status. Refresh the page to view the latest batch status.",
            CANNOT_MODIFY_BACS_BATCH_THAT_IS_NOT_A_DRAFT:
                "Unable to modify batch because it is not in draft status. Refresh the page to view the latest batch status.",
        },
    },
    auth: {
        "Sign In": "Login", // Tab header
        "Sign in": "Login", // Button label
        "User is disabled.":
            "Standard login is disabled for this account. Please use 'Login with SSO' to continue.", // Validation message
        "User does not exist.": "Incorrect username or password.",
    },
    statusDescriptions: {
        failureReason: "Failure reason",
        failureReasons: {
            INSUFFICIENT_FUNDS: "Insufficient funds in the payer's account.",
            EXPIRED_CARD:
                "The card used has expired and could not be used to complete this payment.",
            RESTRICTED_CARD:
                "The card used is restricted and could not be used to complete this payment.",
            CARD_DECLINED: "The payment was declined by the card issuer.",
            AUTHENTICATION_REJECTED_BY_CARD_ISSUER_3D_AUTH_FAILED:
                "The card issuer rejected the 3D Secure authentication.",
            CANNOT_AUTHORISE_CARD_3D_AUTH_FAILED:
                "The payment could not be authorised because 3D Secure authentication failed.",
            SUSPECTED_FRAUD: "The payment was declined due to suspected fraud.",
            TRANSACTION_NOT_PERMITTED:
                "The payment was declined because it is not permitted by the card issuer.",
            TRANSACTION_NOT_FOUND: "The payment was not completed",
            TRANSACTION_TIMED_OUT: "The transaction timed out.",
            CARD_NOT_SUPPORTED:
                "The card number used is not supported as a card type.",
            AWAITING_3D_AUTHENTICATION:
                "The 3D Secure authentication was not completed.",
            INVALID_POSTCODE: "The postcode used is invalid.",
            SURNAME_TOO_LONG: "The surname used is too long.",
            BILLING_STATE_REQUIRED: "Missing billing state in payer's address.",
            EXCESSIVE_SURCHARGE: "The surcharge applied is excessive.",
            PROVIDER_ERROR: "An error occurred with the payment provider.",
            EXPIRED_PAYMENT:
                "The payment session expired before the transaction was authorised.",
            AUTHORISATION_CANCELLED: "The payment authorisation was cancelled.",
            REJECTED_BY_PROVIDER:
                "The payment was rejected by the payment provider for an unspecified reason.",
            AUTHORISATION_NOT_PROVIDED:
                "The payer did not authorise the payment.",
            INVALID_ACCOUNT_HOLDER_NAME:
                "The account holder's name details were invalid.",
            REJECTED: "The payment was rejected for an unspecified reason.",
            PROVIDER_EXPIRED:
                "The payment failed because the token or exchange code used to communicate with the bank expired.",
            BLOCKED_AT_PROVIDER_LEVEL:
                "The payment has been blocked due to a regulatory requirement. This may happen if the payer fails one of the screening checks.",
            PAYMENT_PROVIDER_ERROR:
                "An error occurred with the payment provider.",
            INVALID_ACCOUNT_DETAILS:
                "The payment failed because either the payer's or payee's account details were invalid.",
            INVALID_BENEFICIARY:
                "The payment failed because an invalid beneficiary account reference was provided.",
            INVALID_CARD:
                "The payment failed because an invalid card was used.",
            INVALID_CREDENTIALS:
                "The banking credentials provided by the payer to log into their bank were incorrect.",
            INVALID_OTP:
                "The payer submitted an incorrect one-time password during the authorisation of the payment.",
            PAYMENT_LIMIT_EXCEEDED:
                "The payer's payment limit amount with their bank was breached.",
            UNKNOWN_ERROR: "The payment failed for an unknown reason.",
            PAYOUT_FAILED: "The payout process failed.",
            INCORRECT_CVV: "The payer entered an incorrect CVV.",
            CLOSED_ACCOUNT:
                "The card being used is associated with a closed account.",
            CARD_BLOCKED: "The card has been blocked by the card issuer.",
            INVALID_AMOUNT: "The payment amount is invalid.",
            ISSUER_UNAVAILABLE: "The card issuer is unavailable.",
            ADDRESS_MISMATCH:
                "The address provided does not match the card issuer's records.",
            ONLINE_PIN_REQUIRED:
                "An online PIN was required to complete this payment.",
            MOBILE_PIN_REQUIRED:
                "A mobile PIN was required to complete this payment.",
            RECURRING_TOKEN_REVOKED: "The recurring payment token was revoked.",
            ISSUER_COUNTRY_MISMATCH:
                "The card issuer's country does not match the country required for this payment.",
            UNSUPPORTED_CARD_TYPE:
                "The type of card used is not supported for this payment.",
            CORPORATE_CARD:
                "The payment was declined because a corporate card was used.",
            QUOTE_NOT_VALID: "The payment quote is no longer valid.",
        },
    },
    transactions: {
        lloyds: {
            refundTitle: "Refund transaction: {transactionId}",
            refundTransaction:
                "You are about to refund this transaction. You can adjust the amount below to issue a partial refund.",
            refundAction:
                "This action will reverse the payment associated with it:",
            ifPaymentMadeToday:
                "If the payment was made today, it will be voided (cancelled before settlement).",
            ifPaymentMadePreviousDay:
                "If the payment was made on a previous day, it will be refunded to the payer's account.",
            onceProcessed:
                "Once processed, this action cannot be undone. Please confirm the refund amount below before continuing.",
        },
        adyen: {
            refundTitle: "Refund transaction: {transactionId}",
            partialRefund: {
                refundTransaction:
                    "You are about to refund this transaction. You can adjust the amount below to issue a partial refund.",
                refundAction:
                    "This action will return the payment to the payer's account.",
                onceProcessed:
                    "Once processed, this refund cannot be undone. Please confirm the refund amount below before continuing.",
            },
            fullRefund: {
                refundTitle: "Refund transaction: {transactionId}",
                refundTransaction: "You are about to refund this transaction.",
                refundAction:
                    "This action will return the payment to the payer's account.",
                onceProcessed: "Once processed, this refund cannot be undone.",
            },
        },
    },
};

const i18n = createI18n({
    legacy: false,
    locale: userLocale,
    fallbackLocale: "en-GB",
    messages: {
        "en-GB": enTranslations,
    },
    datetimeFormats: {
        "en-GB": {
            short: {
                year: "numeric",
                month: "short",
                day: "numeric",
            },
            long: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
            },
        },
    },
});

export const translationExists = (key: string): boolean => {
    /**
     * Checks if the key exist in the list of internationalised messages.
     * It check in the "en-GB" locale for now as we do not have any other
     * translation languages enabled. Once we add more language support, we
     * can change the "en-GB" value to the user's locale
     * @param {string} key
     * @returns {boolean}
     */
    // TODO change "en-GB" to userLocale when we add support for more languages.
    return i18n.global.te(key, "en-GB");
};

export default i18n;

/** AWS Amplify labels */
I18n.putVocabularies(translations);
I18n.putVocabularies({
    en: enTranslations.auth,
});
